import { Static, TSchema, Type } from "@fastify/type-provider-typebox";
import { BxError } from "./errors";

// These are the listing conditions as Amazon stores them in the reports.
export const LISTING_CONDITIONS = {
  1: "used_like_new",
  2: "used_very_good",
  3: "used_good",
  4: "used_acceptable",
  5: "collectible_like_new",
  6: "collectible_very_good",
  7: "collectible_good",
  8: "collectible_acceptable",
  9: "not_used",
  10: "refurbished_refurbished",
  11: "new_new",
};

export enum ListingInboundEligibilityStatus {
  UNKNOWN = "UNKNOWN",
  ELIGIBLE = "ELIGIBLE",
  NOT_ELIGIBLE = "NOT_ELIGIBLE"
}

export enum BuyCostType {
  FIXED = "FIXED",
  PERIOD = "PERIOD"
}

export const InventoryAnalyticsRequestParams = Type.Object({
  timestamp: Type.Optional(Type.String()),
});

export type InventoryAnalyticsRequestParams = Static<typeof InventoryAnalyticsRequestParams>;

export const InventoryAnalyticsDataPoints = Type.Object({
  totalInStockQuantity: Type.Number(),
  inStockBuyCost: Type.Number(),
  inStockSalesValue: Type.Number(),
  totalSKUs: Type.Number()
});

export const InventoryAnalyticsResponse = Type.Object({
  fba: InventoryAnalyticsDataPoints,
  fbm: InventoryAnalyticsDataPoints,
  totalSKUs: Type.Optional(Type.Number()),
  lastSynced: Type.Optional(Type.Number()),
});
export type InventoryAnalyticsResponse = Static<typeof InventoryAnalyticsResponse>;

export const SubmitListingsResponse = Type.Array(Type.Object({
  data: Type.Optional(Type.Object({
    sku: Type.String(),
    status: Type.Union([Type.Literal("ACCEPTED"), Type.Literal("INVALID"), Type.Literal("VALID")]),
    submissionId: Type.String(),
    issues: Type.Array(Type.Object({
      code: Type.String(),
      message: Type.String(),
      severity: Type.Union([Type.Literal("ERROR"), Type.Literal("WARNING"), Type.Literal("INFO")]), 
      attributeNames: Type.Optional(Type.Array(Type.String())),  // names of attributes associated with the issue, if applicable
      categories: Type.Array(Type.Union([Type.Literal("INVALID_ATTRIBUTE"), Type.Literal("MISSING_ATTRIBUTE"), Type.Literal("INVALID_IMAGE"), Type.Literal("MISSING_IMAGE"), Type.Literal("INVALID_PRICE"), Type.Literal("MISSING_PRICE"), Type.Literal("DUPLICATE"), Type.Literal("QUALIFICATION_REQUIRED")])),
      enforcements: Type.Optional(Type.Object({
        actions: Type.Array(Type.Union([Type.Literal("LISTING_SUPPRESSED"), Type.Literal("ATTRIBUTE_SUPPRESSED"), Type.Literal("CATALOG_ITEM_REMOVED"), Type.Literal("SEARCH_SUPPRESSED")])),
        exemption: Type.Object({
          status: Type.Union([Type.Literal("EXEMPT"), Type.Literal("EXEMPT_UNTIL_EXPIRY_DATE"), Type.Literal("NOT_EXEMPT")]),
          expiryDate: Type.Optional(Type.String()), // ISO 8601 datestring @TODO(nick) should this be a typebox date type?
        })
      }))
    })),
  })),
  errors: Type.Optional(Type.Array(BxError)),
}));
export type SubmitListingsResponse = Static<typeof SubmitListingsResponse>;

export const SubmitListingsBody = Type.Array(
  Type.Object({
    // these are required for submitting to the API:
    name: Type.Optional(Type.String()),
    image: Type.Optional(Type.String()),
    asin: Type.String(),
    sku: Type.String(),
    condition: Type.String(),
    // quantity: Type.Number({ minimum: 1 }), // @TODO we might implement this one later. for now it's not necessary - quantity is updated when making shipments
    priceUSD: Type.String(), // format like: "100.00",
    submittedAt: Type.String(),

    conditionNotes: Type.Optional(Type.String()),
    buyCost: Type.Optional(Type.String()),
    datePurchased: Type.Optional(Type.String()),
    expirationDate: Type.Optional(Type.String()),
    supplier: Type.Optional(Type.String({ maxLength: 200 })),
  })
);
export type SubmitListingsBody = Static<typeof SubmitListingsBody>;

export const SubmitFbmListingsBody = Type.Array(
  Type.Object({
    // these are required for submitting to the API:
    asin: Type.String(),
    sku: Type.String(),
    name: Type.Optional(Type.String()),
    image: Type.Optional(Type.String()),
    condition: Type.String(),
    submittedAt: Type.String(),
    quantity: Type.Number(),
    shippingTemplateId: Type.Number(),
    priceUSD: Type.String(), // format like: "100.00",
    conditionNotes: Type.Optional(Type.String()),
    buyCost: Type.Optional(Type.String()),
    datePurchased: Type.Optional(Type.String()),
    expirationDate: Type.Optional(Type.String()),
    supplier: Type.Optional(Type.String({ maxLength: 200 })),
    replenish: Type.Optional(Type.Boolean()),
  })
);
export type SubmitFbmListingsBody = Static<typeof SubmitListingsBody>;

export const GetListingRestrictionsBody = Type.Array(Type.Object({
  asin: Type.String(),
  condition: Type.String(),
}));
export type GetListingRestrictionsBody = Static<typeof GetListingRestrictionsBody>;

const Nullable = <T extends TSchema>(schema: T) => Type.Union([schema, Type.Null()]) // @TODO move this somewhere else

export const GetListingRestrictionsResponse = Type.Array(Nullable(Type.Array(BxError)));
export type GetListingRestrictionsResponse = Static<typeof GetListingRestrictionsResponse>;

export const PollingFbaListingsResponse = Type.Array(Type.Object({
  sku: Type.String(),
  issues: Type.Unknown(), //Issues are stored on the listings as a strigified JSON payload.
  strandedReason: Type.Optional(Type.String()),
  strandedDate: Type.Optional(Type.String()),
  strandedRemovalDate: Type.Optional(Type.String()),
  status: Type.String(),
}));
export type PollingFbaListingsResponse = Static<typeof PollingFbaListingsResponse>;

export const PollingFbmListingsResponse = Type.Array(Type.Object({
  sku: Type.String(),
  issues: Type.Unknown(), //Issues are stored on the listings as a strigified JSON payload.
  status: Type.String(),
}));
export type PollingFbmListingsResponse = Static<typeof PollingFbaListingsResponse>;